// Per-task colour helpers for the Gantt. Genba tasks carry a `#RRGGBB` hex
// (usually a pastel); the bar fill uses it directly, while the border and the
// progress band need a darker, saturated shade derived from it so they read
// against the pastel fill.

import type { ISchedulerTask, TaskId } from '@interfaces/IScheduler';

/** A CSS hex colour literal. The validated form is always `#RRGGBB` — use
    `isHexColor` to narrow arbitrary strings into it. */
export type HexColor = `#${string}`;

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** Type guard: a usable `#RRGGBB` colour string. */
export function isHexColor(value: string | undefined): value is HexColor {
  return typeof value === 'string' && HEX_RE.test(value);
}

/**
 * Darken a `#RRGGBB` hex by mixing each channel toward black.
 * `amount` is 0..1 (0 = unchanged, 1 = black). Invalid input is returned as-is.
 */
export function darkenHex(hex: string, amount: number): string {
  if (!HEX_RE.test(hex)) return hex;
  const clamped = Math.max(0, Math.min(1, amount));
  const darkenChannel = (i: number): string => {
    const channelValue = parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
    return Math.round(channelValue * (1 - clamped)).toString(16).padStart(2, '0');
  };
  return `#${darkenChannel(0)}${darkenChannel(1)}${darkenChannel(2)}`;
}

/**
 * Lighten a `#RRGGBB` hex by mixing each channel toward white.
 * `amount` is 0..1 (0 = unchanged, 1 = white). Invalid input is returned as-is.
 * Used by auto-colorize to tint deeper hierarchy levels progressively brighter.
 */
export function lightenHex(hex: string, amount: number): string {
  if (!HEX_RE.test(hex)) return hex;
  const clamped = Math.max(0, Math.min(1, amount));
  const lightenChannel = (i: number): string => {
    const channelValue = parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
    return Math.round(channelValue + (255 - channelValue) * clamped).toString(16).padStart(2, '0');
  };
  return `#${lightenChannel(0)}${lightenChannel(1)}${lightenChannel(2)}`;
}

// A colourless task renders white, with a neutral border + progress band so the
// bar still reads against the (white) grid. These are tokenised, not raw hex.
export const NO_COLOR_FILL = 'var(--color-surface)';
const NO_COLOR_STROKE = 'var(--g400)';

// Default milestone colour — a milestone is a fixed-date marker, not a trade
// task, so it reads as the universal red diamond unless the auto-colorize panel
// overrides the scheduler-wide milestone colour (ui.milestoneColor).
export const MILESTONE_FILL = 'var(--red)';

/**
 * Resolve the inline colours for a task bar. A task with a custom hex uses it as
 * the fill, with a darkened shade for the border + progress band; a colourless
 * task renders white.
 */
export function taskBarColors(color: string | undefined): { fill: string; stroke: string } {
  if (isHexColor(color)) {
    return { fill: color, stroke: darkenHex(color, 0.34) };
  }
  return { fill: NO_COLOR_FILL, stroke: NO_COLOR_STROKE };
}

/**
 * The colour that appears most often among a group's task colours (the mode).
 * Ties resolve to the first colour to reach the max (stable by encounter order).
 * Returns undefined when no task in the group carries a colour.
 *
 * Milestones are skipped — their colour is a fixed marker hue (ui.milestoneColor),
 * not a trade band, so it must never become a group's representative colour.
 */
export function groupModeColor(tasks: ReadonlyArray<{ color?: string; isMilestone?: boolean }>): string | undefined {
  const counts = new Map<string, number>();
  let best: string | undefined;
  let bestCount = 0;
  for (const task of tasks) {
    if (task.isMilestone) continue;
    if (!isHexColor(task.color)) continue;
    const colorKey = task.color.toUpperCase();
    const count = (counts.get(colorKey) ?? 0) + 1;
    counts.set(colorKey, count);
    if (count > bestCount) {
      bestCount = count;
      best = colorKey;
    }
  }
  return best;
}

/**
 * Resolve a task's effective fill colour by walking the hierarchy, so a
 * sub-task inherits from the nearest ancestor that carries a colour before
 * falling back to its group:
 *
 *   own `color` → nearest ancestor task's `color` (up the `parentId` chain)
 *   → the group's colour (`groupColorById`) → undefined (caller paints white).
 *
 * This replaces the old flat rule (leaf = own colour only; summary = own ?? group)
 * which ignored nesting — a coloured parent never reached its descendants.
 *
 * `taskById` and `groupColorById` are built once per layout pass by the caller.
 * The walk is bounded by a `seen` set so a malformed parent cycle can't hang.
 */
export function resolveTaskColor(
  task: ISchedulerTask,
  taskById: ReadonlyMap<TaskId, ISchedulerTask>,
  groupColorById: ReadonlyMap<string, string>,
): string | undefined {
  let current: ISchedulerTask | undefined = task;
  const seen = new Set<TaskId>();
  while (current && !seen.has(current.id)) {
    if (isHexColor(current.color)) return current.color;
    seen.add(current.id);
    current = current.parentId != null ? taskById.get(current.parentId) : undefined;
  }
  const groupColor = groupColorById.get(task.groupId);
  return isHexColor(groupColor) ? groupColor : undefined;
}

/**
 * Group bar colour: the mode of its tasks' colours, or white when none of its
 * tasks carry a colour. Groups have no colour of their own — it's derived purely
 * from the tasks.
 */
export function groupBarColor(tasks: ReadonlyArray<{ color?: string }>): string {
  return groupModeColor(tasks) ?? NO_COLOR_FILL;
}

/**
 * Resolve a group's bar colour straight from the full task list: filters the
 * group's member tasks, then applies the group-colour rule (mode of member
 * colours, white when none are coloured).
 */
export function groupColorForGroup(tasks: readonly ISchedulerTask[], groupId: string): string {
  return groupBarColor(tasks.filter((task) => task.groupId === groupId));
}
